import { useMemo } from 'react';
import { cellServiceOptions } from '../marketplace-custom-config';

const usePropertyAccess = ({ arrivalInstructions, policies, intl }) => {
  const features = useMemo(() => {
    if (!arrivalInstructions && !policies) return [];

    const result = [];
    const accessItems = [];

    // Check In
    if (arrivalInstructions?.checkInTime) {
      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.checkIn' })}:`,
        subtitle: intl.formatMessage(
          { id: 'ListingPage.rules.checkInAfter' },
          { time: arrivalInstructions.checkInTime }
        ),
        isInline: true,
      });
    }

    // Check Out
    if (arrivalInstructions?.checkOutTime) {
      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.checkOut' })}:`,
        subtitle: intl.formatMessage(
          { id: 'ListingPage.rules.checkOutBefore' },
          { time: arrivalInstructions.checkOutTime }
        ),
        isInline: true,
      });
    }

    // Drive-in Access (duplicate from rules for access section)
    if (policies?.hasDriveInAccess !== undefined) {
      const driveInSubtitle = policies.hasDriveInAccess
        ? intl.formatMessage({ id: 'ListingPage.rules.yes' })
        : intl.formatMessage({ id: 'ListingPage.rules.no' });

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.driveInAccess' })}:`,
        subtitle: driveInSubtitle,
        isInline: true,
      });
    }

    // Parking
    if (policies?.allowsParking !== undefined) {
      const parkingSubtitle = policies.allowsParking
        ? intl.formatMessage({ id: 'ListingPage.rules.yes' })
        : intl.formatMessage({ id: 'ListingPage.rules.no' });

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.parking' })}:`,
        subtitle: parkingSubtitle + (policies.parkingInfo ? ` • ${policies.parkingInfo}` : ''),
        isInline: true,
      });
    }

    // High Vehicle Ground Clearance Required
    if (policies?.highVehicleClearanceRequired !== undefined) {
      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.highVehicleClearance' })}:`,
        subtitle: policies.highVehicleClearanceRequired
          ? intl.formatMessage({ id: 'ListingPage.rules.yes' })
          : intl.formatMessage({ id: 'ListingPage.rules.no' }),
        isInline: true,
      });
    }

    // Walk-in Access
    if (policies?.hasWalkInAccess !== undefined) {
      const walkInSubtitle = policies.hasWalkInAccess
        ? intl.formatMessage({ id: 'ListingPage.rules.yes' })
        : intl.formatMessage({ id: 'ListingPage.rules.no' });

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.walkInAccess' })}:`,
        subtitle: walkInSubtitle,
        isInline: true,
      });
    }

    // Cell Service
    if (arrivalInstructions?.hasCellService !== undefined) {
      const { hasCellService } = arrivalInstructions;
      let cellServiceSubtitle = '';

      if (hasCellService) {
        const carriers = arrivalInstructions.cellService || [];
        if (carriers.length > 0) {
          // Map carrier keys to labels
          const carrierLabels = carriers
            .map(key => {
              const option = cellServiceOptions.find(opt => opt.key === key);
              return option ? option.label : key;
            })
            .join(', ');

          cellServiceSubtitle = `${intl.formatMessage({
            id: 'ListingPage.rules.yes',
          })} • ${carrierLabels}`;

          // Add additional info if provided
          if (arrivalInstructions.cellServiceInfo) {
            cellServiceSubtitle += ` • ${arrivalInstructions.cellServiceInfo}`;
          }
        } else {
          cellServiceSubtitle = intl.formatMessage({ id: 'ListingPage.rules.yes' });
          if (arrivalInstructions.cellServiceInfo) {
            cellServiceSubtitle += ` • ${arrivalInstructions.cellServiceInfo}`;
          }
        }
      } else {
        cellServiceSubtitle = intl.formatMessage({ id: 'ListingPage.rules.no' });
      }

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.cellService' })}:`,
        subtitle: cellServiceSubtitle,
        isInline: true,
      });
    }

    // Nearby Interstate
    if (arrivalInstructions?.milesToInterstate) {
      const interstateSubtitle =
        arrivalInstructions.milesToInterstate !== 'N/A'
          ? `${intl.formatMessage({ id: 'ListingPage.rules.yes' })} • ${intl.formatMessage(
              { id: 'ListingPage.rules.miles' },
              { miles: arrivalInstructions.milesToInterstate }
            )} mi`
          : intl.formatMessage({ id: 'ListingPage.rules.no' });

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.nearbyInterstate' })}:`,
        subtitle: interstateSubtitle,
        isInline: true,
      });
    }

    // Nearby Highway
    if (arrivalInstructions?.milesToHighway) {
      const highwaySubtitle =
        arrivalInstructions.milesToHighway !== 'N/A'
          ? `${intl.formatMessage({ id: 'ListingPage.rules.yes' })} • ${intl.formatMessage(
              { id: 'ListingPage.rules.miles' },
              { miles: arrivalInstructions.milesToHighway }
            )} mi`
          : intl.formatMessage({ id: 'ListingPage.rules.no' });

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.nearbyHighway' })}:`,
        subtitle: highwaySubtitle,
        isInline: true,
      });
    }

    // Nearby Airport
    if (arrivalInstructions?.milesToAirport) {
      let airportSubtitle = '';

      if (arrivalInstructions.milesToAirport) {
        airportSubtitle = `${intl.formatMessage({ id: 'ListingPage.rules.yes' })} • `;

        if (arrivalInstructions.milesToAirportInfo) {
          airportSubtitle += arrivalInstructions.milesToAirportInfo;
        } else {
          airportSubtitle += intl.formatMessage(
            { id: 'ListingPage.rules.miles' },
            { miles: arrivalInstructions.milesToAirport }
          );
        }
      } else {
        airportSubtitle = intl.formatMessage({ id: 'ListingPage.rules.no' });
      }

      accessItems.push({
        label: `${intl.formatMessage({ id: 'ListingPage.rules.nearbyAirport' })}:`,
        subtitle: airportSubtitle,
        isInline: true,
      });
    }

    if (accessItems.length > 0) {
      result.push({
        items: accessItems,
      });
    }

    return result;
  }, [arrivalInstructions, policies, intl]);

  return { features };
};

export default usePropertyAccess;
